import { useEffect, useMemo } from 'react';

import { useIzteachSessionStore } from '../../stores/izteach';
import { useQltSlotAttendanceStore, useQltTimetableStore } from '../../stores/qlt';
import { resolvePeriodCourseScope } from '../periodLabels';
import type { QuizResultsToggle } from '../quiz/QuizControls';
import { rosterOf } from '../submissions/submissions';
import type { BoardContextValue } from '../useBoardContext';
import { examLoadKey, examResultsAction } from './classExams';
import { useExamResultStore } from './store';

// Kết quả on a test unit's rail: the class's real results for its exam, in the card Bài nộp opens
export function useUnitExamResults(context: BoardContextValue, examId: string | null): QuizResultsToggle {
  const cells = useQltTimetableStore((state) => state.todayCells);
  // same scope as Bài nộp: a board opened by URL takes grade and subject from the timetable
  const scope = resolvePeriodCourseScope({ gradeId: context.gradeId, subjectId: context.subjectId }, cells, context);
  const rows = useQltSlotAttendanceStore((state) => state.rows);
  const signedIn = useIzteachSessionStore((state) => Boolean(state.accessToken));
  const openExamId = useExamResultStore((state) => state.examId);
  const codes = useMemo(() => rosterOf(rows).map((student) => student.code), [rows]);
  const input = { codes, classId: context.classId, gradeId: scope.gradeId, subjectId: scope.subjectId };
  const key = examLoadKey(input, signedIn);
  const open = examResultsAction(openExamId, examId) === 'close';

  // a roster or token arriving while the card is open loads again; the store skips a repeat key
  useEffect(() => { if (open && codes.length > 0) void useExamResultStore.getState().load(key, input); }, [open, key]);

  const onToggle = () => {
    const store = useExamResultStore.getState();
    const action = examResultsAction(store.examId, examId);
    if (action === 'close') store.close();
    if (action !== 'open' || !examId) return;
    // load first, so the card opens on its spinner rather than an empty exam
    if (codes.length > 0) void store.load(key, input);
    void store.open(examId);
  };
  return { open, onToggle };
}
